// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@pace/XDL/constants";
import { safeJsonParse } from "@pace/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";

const enum fields {
    direction = "direction",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "row" | "column";
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    warehouseInformation: safeJsonParse(state.context.session.properties["headerWarehouseInformation"]) || {},
    isAuthenticated: xIsAuthenticated(state),
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HeaderWarehouseInformationStyle {
    wrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    label?: TypographyProps;
    link?: LinkPresentationProps;
}

export const headerWarehouseInformationStyle: HeaderWarehouseInformationStyle = {};

const styles = headerWarehouseInformationStyle;

const HeaderWarehouseInformation: FC<Props> = ({
    fields,
    isAuthenticated,
    warehouseInformation,
    isCheckoutPage,
}: Props) => {
    return (
        <>
            {isAuthenticated && (
                <StyledWrapper {...styles.wrapper} {...{ direction: fields.direction, isCheckoutPage }}>
                    {warehouseInformation.Email && (
                        <StyledWrapper {...styles.sectionWrapper}>
                            <Typography {...styles.label}>{translate("Branch Email:")}</Typography>
                            <Link href={`mailto:${warehouseInformation.Email}`} {...styles.link}>
                                {warehouseInformation.Email}
                            </Link>
                        </StyledWrapper>
                    )}
                    {warehouseInformation.Phone && (
                        <StyledWrapper {...styles.sectionWrapper}>
                            <Typography {...styles.label}>{translate("Branch Phone:")}</Typography>
                            <Link href={`tel:${warehouseInformation.Phone}`} {...styles.link}>
                                {warehouseInformation.Phone}
                            </Link>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderWarehouseInformation),
    definition: {
        displayName: "Header Warehouse Information",
        icon: "location-dot",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "row",
                fieldType: "General",
                options: [
                    {
                        displayName: "Display In Row",
                        value: "row",
                    },
                    {
                        displayName: "Display In Column",
                        value: "column",
                    },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
